import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import {
  CatalogWorkbookError,
  downloadCatalogTemplate,
  parseCatalogWorkbook,
  type CatalogKind
} from "./catalogWorkbook";

interface CatalogImportResult {
  imported_count: number;
  condition_revision: number;
}

const LABELS: Record<CatalogKind, string> = {
  courses: "课程",
  rooms: "功能教室",
  teachers: "教师",
  classes: "班级"
};

export function CatalogImportActions({
  projectId,
  kind,
  conditionRevision,
  locked,
  listQueryKey
}: {
  projectId: string;
  kind: CatalogKind;
  conditionRevision?: number;
  locked: boolean;
  listQueryKey: ReadonlyArray<unknown>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<
    { tone: "success" | "error"; message: string } | null
  >(null);
  const label = LABELS[kind];
  const mutation = useMutation({
    mutationFn: async (file: File) => {
      if (conditionRevision === undefined) {
        throw new Error("基础资料尚未加载完成");
      }
      const workbook = await parseCatalogWorkbook(file, kind);
      return apiRequest<CatalogImportResult>(
        `/projects/${projectId}/catalog-imports`,
        {
          method: "POST",
          body: JSON.stringify({
            ...workbook,
            expected_condition_revision: conditionRevision
          })
        }
      );
    },
    onSuccess: async (result) => {
      setFeedback({
        tone: "success",
        message: `已追加导入 ${result.imported_count} 条${label}信息`
      });
      await queryClient.invalidateQueries({ queryKey: [...listQueryKey] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: async (error) => {
      setFeedback({ tone: "error", message: importErrorMessage(error, kind) });
      if (error instanceof ApiError && error.code === "REVISION_CONFLICT") {
        await queryClient.invalidateQueries({ queryKey: [...listQueryKey] });
        await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      }
    }
  });

  async function selectFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setFeedback(null);
    await mutation.mutateAsync(file).catch(() => undefined);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="catalog-import-actions">
      <button
        className="ghost-button"
        type="button"
        onClick={() => {
          setFeedback(null);
          void downloadCatalogTemplate(kind).catch((error: unknown) => {
            setFeedback({
              tone: "error",
              message: error instanceof Error ? error.message : "模板下载失败"
            });
          });
        }}
      >
        下载{label}模板
      </button>
      <button
        className="ghost-button"
        type="button"
        disabled={locked || conditionRevision === undefined || mutation.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {mutation.isPending ? "正在导入…" : `导入${label}`}
      </button>
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        aria-label={`选择${label}信息 Excel 文件`}
        disabled={locked || conditionRevision === undefined || mutation.isPending}
        onChange={(event) => void selectFile(event.target.files?.[0])}
      />
      {feedback ? (
        <p
          className={`catalog-import-feedback ${feedback.tone}`}
          role={feedback.tone === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}

function importErrorMessage(error: unknown, kind: CatalogKind): string {
  if (error instanceof CatalogWorkbookError) {
    return locationMessage(error.message, {
      sheetName: error.sheetName,
      sourceRow: error.sourceRow,
      field: catalogFieldLabel(kind, error.field)
    });
  }
  if (error instanceof ApiError) {
    const rowErrors = (error.details as Record<string, unknown> | undefined)?.row_errors;
    const first = Array.isArray(rowErrors) ? rowErrors[0] : undefined;
    if (isRecord(first)) {
      return locationMessage(String(first.message ?? error.message), {
        sheetName: String(
          (error.details as Record<string, unknown> | undefined)?.sheet_name ?? ""
        ),
        sourceRow:
          typeof first.source_row === "number" ? first.source_row : undefined,
        field:
          typeof first.field === "string"
            ? catalogFieldLabel(kind, first.field)
            : undefined
      });
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "导入失败，请检查文件后重试";
}

function catalogFieldLabel(kind: CatalogKind, field: string | undefined): string | undefined {
  if (!field) return undefined;
  const labels: Record<CatalogKind, Record<string, string>> = {
    courses: { name: "课程名称" },
    rooms: { name: "功能教室名称", count: "间数" },
    teachers: {
      name: "教师姓名",
      planned_lessons: "课时数",
      max_daily_lessons: "每天最大课时数"
    },
    classes: { grade_label: "年级", class_name: "班级名称" }
  };
  return labels[kind][field] ?? field;
}

function locationMessage(
  message: string,
  location: { sheetName?: string; sourceRow?: number; field?: string }
): string {
  const parts = [
    location.sheetName ? `工作表“${location.sheetName}”` : "",
    location.sourceRow ? `第 ${location.sourceRow} 行` : "",
    location.field ? `字段“${location.field}”` : ""
  ].filter(Boolean);
  return parts.length ? `${parts.join("，")}：${message}` : message;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
